import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getChannelNextMessagesLink,
  getCurrentChannel,
  getCurrentChannelMessages,
} from 'app/store/Chat/selectors';
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useState,
} from 'react';
import {
  createChatMessageEffect,
  fetchChatChannelMessagesEffect,
} from 'app/store/Chat/effects';
import { getUserGuid } from 'app/utils/helpers';
import { VirtuosoHandle } from 'react-virtuoso';
import { MessageType } from 'types/chat.types';
import { addAlertAction } from 'app/store/Alerts/slice';
import { MessagesList } from '../Chat/MessagesList';
import { SongChatEmptyState } from './SongChatEmptyState';
import { scrollToIndex } from '../Chat/utils';

interface MessagesProps {
  virtuosoRef: RefObject<VirtuosoHandle>;
  setDeleteMessage: Dispatch<SetStateAction<MessageType | undefined>>;
}

export const Messages = ({ virtuosoRef, setDeleteMessage }: MessagesProps) => {
  const dispatch = useAppDispatch();
  const messages = useAppSelector(getCurrentChannelMessages);
  const nextMessagesLink = useAppSelector(getChannelNextMessagesLink);
  const currentChannel = useAppSelector(getCurrentChannel);
  const userGuid = useAppSelector(getUserGuid);

  const [loadingMore, setLoadingMore] = useState(false);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      await dispatch(
        fetchChatChannelMessagesEffect({ next: nextMessagesLink })
      ).unwrap();
    } catch (e) {
      // do nothing
    } finally {
      setLoadingMore(false);
    }
  }, [dispatch, nextMessagesLink]);

  const handleMessageRetry = async (message: MessageType) => {
    try {
      if (currentChannel?.guid && message.text) {
        await dispatch(
          createChatMessageEffect({
            channelGuid: currentChannel?.guid,
            message: message.text,
            messageGuid: message.guid,
          })
        );
        scrollToIndex(virtuosoRef, Number.MAX_SAFE_INTEGER);
      }
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to retry message sending. Try again later.',
        })
      );
    }
  };

  const messageActions = {
    handleDelete: setDeleteMessage,
    handleRetry: handleMessageRetry,
  };

  const fake: MessageType = {
    guid: 'ignore',
    needRetry: false,
    created: { iso: '', timestamp: 0 },
    updated: { iso: '', timestamp: 0 },
  };

  return (
    <MessagesList
      messages={messages.length > 0 ? [fake, ...messages] : []}
      emptyStateComponent={SongChatEmptyState}
      hasMore={Boolean(nextMessagesLink)}
      loadMore={loadMore}
      hasMoreNewer={false}
      loadingMore={loadingMore}
      loadingMoreNewer={false}
      lastReadTimestamp={currentChannel?.lastReadMessageDate?.timestamp}
      userGuid={userGuid!}
      messageActions={messageActions}
      virtuosoRef={virtuosoRef}
    />
  );
};
